import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, FileText, Loader2 } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { SITE_URL } from "../lib/constants";
import { systemSpecs } from "../lib/catalog";
import { PRODUCT_PAGES } from "../data/products";
import { Crumbs } from "../components/product/ProductHero";
import { PhoneLink, QuoteButton, SectionHeading, WhatsAppButton } from "../components/home/CtaButtons";
import { SystemBadges } from "../components/catalog/ProfileSystemsSection";
import { IsdeTeaser } from "../components/product/IsdeTeaser";
import NotFound from "./NotFound";

const PAGE_BY_KEY = Object.fromEntries(PRODUCT_PAGES.filter((p) => p.priceKey).map((p) => [p.priceKey, p]));
const onderwerpOf = (s) => PAGE_BY_KEY[s.product_keys?.[0]]?.onderwerp;

const productJsonLd = (s, path) => ({
  "@context": "https://schema.org", "@type": "Product", name: s.name, description: s.seo_description || s.summary, url: SITE_URL + path,
  category: "Kozijnprofielsysteem", material: s.material,
  ...(s.manufacturer ? { brand: { "@type": "Brand", name: s.manufacturer }, manufacturer: { "@type": "Organization", name: s.manufacturer } } : {}),
  ...(s.images?.length ? { image: s.images.map((i) => (i.startsWith("/") ? SITE_URL + i : imageUrl(i))) } : {}),
  additionalProperty: systemSpecs(s).map(([name, value]) => ({ "@type": "PropertyValue", name, value })),
});

const Body = ({ s, path }) => {
  const crumbs = [{ label: "Home", href: "/" }, ...(PAGE_BY_KEY[s.product_keys?.[0]] ? [{ label: PAGE_BY_KEY[s.product_keys[0]].label, href: PAGE_BY_KEY[s.product_keys[0]].path }] : []), { label: s.name, href: path }];
  useSEO({ title: s.seo_title || `${s.name} | Maas Kozijn`, description: s.seo_description || s.summary, path,
    ogImage: s.images?.[0] && (s.images[0].startsWith("/") ? SITE_URL + s.images[0] : imageUrl(s.images[0])),
    jsonLd: productJsonLd(s, path), breadcrumbs: buildBreadcrumbsJsonLd(crumbs) });
  const params = { systeem: s.slug };
  return (
    <div data-testid={`profile-system-page-${s.slug}`}>
      <section className="relative overflow-hidden bg-white" aria-labelledby="system-title">
        <div className="hero-grid absolute inset-y-0 right-0 hidden w-[45%] lg:block" aria-hidden="true" />
        <div className="container-tight relative grid items-center gap-10 pb-12 pt-6 lg:grid-cols-12 lg:pb-16">
          <div className="reveal lg:col-span-7">
            <Crumbs items={crumbs} />
            <p className="eyebrow mt-6">{s.manufacturer ? `Profielsysteem van ${s.manufacturer}` : "Profielsysteem"}</p>
            <h1 id="system-title" className="h1 mt-3 text-balance">{s.name}</h1>
            {s.summary && <p className="lead mt-5 max-w-2xl">{s.summary}</p>}
            <div className="mt-5"><SystemBadges s={s} /></div>
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <QuoteButton location="profile-system-hero" onderwerp={onderwerpOf(s)} params={params} />
              <WhatsAppButton location="profile-system-hero" />
            </div>
            <div className="mt-4"><PhoneLink location="profile-system-hero" /></div>
          </div>
          {s.images?.[0] && <div className="lg:col-span-5"><div className="frame"><img src={imageUrl(s.images[0])} alt={`Profielsysteem ${s.name}`} className="aspect-[4/3] w-full rounded-2xl object-cover" /></div></div>}
        </div>
      </section>
      {s.page_intro && (
        <section className="section-tight border-t border-slate-200" aria-label="Introductie">
          <div className="container-prose space-y-4 text-[16.5px] leading-relaxed text-slate-700" data-testid="profile-system-intro">
            {s.page_intro.split(/\n\s*\n/).map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
          </div>
        </section>
      )}
      <section className="section-tight bg-slate-50" aria-labelledby="specs-title">
        <div className="container-tight grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading id="specs-title" eyebrow="Technische gegevens" title={`${s.name} in cijfers`} lead="Gegevens volgens de fabrikant. De waarden voor uw kozijn hangen af van de uitvoering, de maten en het glas." />
            <div className="mt-5 flex flex-col items-start gap-2 text-[14px]">
              {s.brochure_url && <a href={s.brochure_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-maas hover:underline"><FileText className="h-4 w-4" aria-hidden="true" /> Brochure van de fabrikant</a>}
              {s.source_url && <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-maas"><ExternalLink className="h-4 w-4" aria-hidden="true" /> Bron: website fabrikant</a>}
            </div>
          </div>
          <dl className="card divide-y divide-slate-100 lg:col-span-8" data-testid="profile-system-specs">
            {systemSpecs(s).map(([k, v]) => <div key={k} className="grid gap-1 px-5 py-4 sm:grid-cols-3 sm:gap-6"><dt className="text-[14px] font-semibold text-ink">{k}</dt><dd className="text-[14.5px] text-slate-600 sm:col-span-2">{v}</dd></div>)}
          </dl>
        </div>
      </section>
      {s.variants?.length > 0 && (
        <section className="section-tight" aria-labelledby="variants-title">
          <div className="container-tight">
            <SectionHeading id="variants-title" eyebrow="Uitvoeringen" title={`Varianten van ${s.name}`} />
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {s.variants.map((v) => <li key={v.name} className="card p-5"><h3 className="text-[16px] font-semibold text-ink">{v.name}</h3>{v.text && <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">{v.text}</p>}</li>)}
            </ul>
          </div>
        </section>
      )}
      {s.product_keys?.some((k) => k.endsWith("_kozijnen")) && <IsdeTeaser path="profiel" />}
      <section className="section-tight border-t border-slate-200" aria-labelledby="usage-title">
        <div className="container-tight">
          <SectionHeading id="usage-title" eyebrow="Toepassing" title={`Waar kiest u ${s.name} voor?`} />
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {s.product_keys.filter((k) => PAGE_BY_KEY[k]).map((k) => (
              <li key={k}><Link to={PAGE_BY_KEY[k].path} className="card card-hover flex items-center justify-between gap-3 p-5 font-semibold text-ink hover:text-maas" data-testid={`profile-system-product-${k}`}>
                {PAGE_BY_KEY[k].label} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link></li>
            ))}
          </ul>
          <div className="mt-10 rounded-3xl bg-ink p-8 text-white sm:p-10">
            <h2 className="font-display text-[clamp(1.4rem,1.2vw+1rem,2rem)] font-bold">Benieuwd wat {s.name} voor uw woning kost?</h2>
            <p className="mt-3 max-w-2xl text-slate-300">Vraag een vrijblijvende prijsindicatie aan. Uw keuze voor {s.name} nemen we automatisch mee.</p>
            <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <QuoteButton location="profile-system-end" onderwerp={onderwerpOf(s)} params={params} />
              <WhatsAppButton location="profile-system-end" dark />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default function ProfileSystemPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let on = true;
    setState({ loading: true, data: null });
    api.get(`/profile-systems/${slug}`).then((r) => on && setState({ loading: false, data: r.data })).catch(() => on && setState({ loading: false, data: null }));
    return () => { on = false; };
  }, [slug]);
  if (state.loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-maas" aria-label="Laden" /></div>;
  if (!state.data) return <NotFound />;
  return <Body s={state.data} path={`/profielsystemen/${slug}`} />;
}
